import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { ReferenceMentionItem } from "./types";
import { GetFilterDataTypeString } from "../../utils/formatters";

/** Imperative handle exposed to the tippy.js popup for keyboard handling. */
export type SuggestionListRef = {
  onKeyDown: NonNullable<
    ReturnType<NonNullable<SuggestionOptions<ReferenceMentionItem>["render"]>>["onKeyDown"]
  >;
};

export type SuggestionListProps = SuggestionProps<ReferenceMentionItem>;

/**
 * The suggestion dropdown shared by the "#" prompt mention and the "." property
 * mention. Renders the current list and supports arrow-key navigation plus
 * Enter to select. The whole item is handed to `command` so the mention
 * extension can carry its full attributes (type, prompt data type, parent).
 */
const SuggestionList = forwardRef<SuggestionListRef, SuggestionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectItem = (index: number) => {
    if (index >= props.items.length) {
      return;
    }
    props.command(props.items[index]);
  };

  const upHandler = () => {
    setSelectedIndex((prev) => (prev + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((prev) => (prev + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  if (props.items.length === 0) {
    return null;
  }

  return (
    <Paper elevation={5}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "4px",
          maxHeight: "350px",
          overflowY: "auto",
          padding: "5px 0",
        }}
      >
        {props.items.map((item, index) => {
          const selected = index === selectedIndex;
          return (
            <Box
              key={`${item.type}-${item.id}-${index}`}
              ref={(el: HTMLDivElement | null) => {
                itemRefs.current[index] = el;
              }}
              onClick={() => selectItem(index)}
              sx={{
                fontSize: "13px",
                fontWeight: 400,
                padding: "4px 16px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "3px",
                backgroundColor: selected ? "#2f70c8" : "transparent",
                color: selected ? "white" : "inherit",
                "&:hover": {
                  backgroundColor: selected ? "#2f70c8" : "#f5f5f5",
                },
              }}
            >
              <Typography variant="body1">{item.label}</Typography>
              {item.type === "filter-answer" && item.promptDataType != null && (
                <Typography
                  variant="caption"
                  sx={{ color: selected ? "rgba(255,255,255,0.85)" : "text.secondary" }}
                >
                  {GetFilterDataTypeString(item.promptDataType)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
});

SuggestionList.displayName = "SuggestionList";

export default SuggestionList;
